<template>
  <div
    class="LLFileModalImage flex items-center justify-center"
    :class="{
      LLFileModalImage_fill: fill,
      LLFileModalImage_loaded: highResVisible,
      LLFileModalImage_failed: failed
    }"
  >
    <div v-if="!highResVisible && !failed" class="LLFileModalImage__loader">
      <LoadingIcon class="LLFileModalImage__loader-icon" :size="48" :border-width="4" />
    </div>
    <img
      v-if="displaySrc"
      class="LLFileModalImage__image"
      :class="{ LLFileModalImage__image_loaded: highResVisible }"
      :src="displaySrc"
      alt=""
      draggable="false"
      @load="onElementLoad"
      @error="onElementError"
    />
  </div>
</template>

<script>
import LoadingIcon from '@/components/common/LoadingIcon'
import { isOriginalLoaded, preloadOriginal, forgetOriginal } from '@/utils/imageLoadCache'

export default {
  name: 'LLFileModalImage',
  components: { LoadingIcon },
  props: {
    file: {
      type: Object,
      required: true
    },
    fill: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      objectUrl: null,
      placeholderSrc: null,
      placeholderReady: false,
      fallbackActive: false,
      isLoaded: false,
      highResVisible: false,
      failed: false,
      startedFromCache: false,
      loadGen: 0
    }
  },
  computed: {
    isTemporary() {
      return !!this.file?.temporary || !!this.file?.isTemporary
    },
    lowResUrl() {
      const thumbnails = (this.file && this.file.thumbnails) || []
      if (!thumbnails.length) {
        return null
      }
      const smallest = thumbnails.reduce((min, thumbnail) => {
        const size = thumbnail.size || 0
        return !min || size < min.size ? { size, location: thumbnail.location } : min
      }, null)
      return smallest && smallest.location
    },
    originalSrc() {
      return this.isTemporary ? this.objectUrl : this.file && this.file.location
    },
    displaySrc() {
      if (this.failed) {
        return null
      }
      return this.isLoaded ? this.originalSrc : this.placeholderSrc
    }
  },
  watch: {
    file: {
      handler() {
        this.resetAndLoad()
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.revokeObjectUrl()
  },
  methods: {
    revokeObjectUrl() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl)
        this.objectUrl = null
      }
    },
    resetAndLoad() {
      this.loadGen += 1
      const gen = this.loadGen
      this.revokeObjectUrl()
      this.placeholderSrc = null
      this.placeholderReady = false
      this.fallbackActive = false
      this.isLoaded = false
      this.highResVisible = false
      this.failed = false
      this.startedFromCache = false
      if (!this.file || (!this.file.location && !this.isTemporary)) {
        this.failed = true
        return
      }
      // Uploaded-but-not-yet-saved files only exist as a browser object URL
      if (this.isTemporary) {
        if (!this.file.temporaryData?.browserFile) {
          this.failed = true
          return
        }
        this.objectUrl = URL.createObjectURL(this.file.temporaryData.browserFile)
        this.isLoaded = true
        return
      }
      // The original was already fully loaded in this session — render it sharp
      // right away, skipping the blur/loader phase entirely.
      if (isOriginalLoaded(this.file)) {
        this.startedFromCache = true
        this.isLoaded = true
        this.highResVisible = true
        return
      }
      this.placeholderSrc = this.lowResUrl
      // Preload the original via JS so the visible <img> never paints it progressively
      preloadOriginal(this.file)
        .then(() => {
          if (gen === this.loadGen && !this._isDestroyed && this.file && !this.failed) {
            this.isLoaded = true
          }
        })
        .catch(() => {
          if (gen === this.loadGen && !this._isDestroyed && this.file && !this.fallbackActive) {
            this.failed = true
          }
        })
    },
    onElementLoad() {
      if (this.isLoaded) {
        this.highResVisible = true
      } else if (this.fallbackActive) {
        // The placeholder failed and the visible <img> was switched to the
        // original — treat this load as the sharp render.
        this.isLoaded = true
        this.highResVisible = true
      } else {
        this.placeholderReady = true
      }
    },
    onElementError() {
      if (this.isLoaded) {
        // A sharp/cached start failed to render (e.g. the browser dropped the
        // image) — forget the cache entry and retry once via the blur path.
        if (this.startedFromCache) {
          this.startedFromCache = false
          forgetOriginal(this.file)
          this.resetAndLoad()
          return
        }
        this.failed = true
      } else {
        // The placeholder failed too — fall back to loading the original directly
        this.placeholderSrc = this.originalSrc
        this.fallbackActive = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.LLFileModalImage {
  @apply relative;

  &__image {
    max-width: 100%;
    max-height: 560px;
    filter: blur(20px);
    transform: scale(1.05);
    transition:
      filter 0.4s ease,
      transform 0.4s ease;
    @apply object-contain object-center select-none;
    &_loaded {
      filter: none;
      transform: none;
    }
  }
  &_fill {
    @apply w-full h-full;
    .LLFileModalImage__image {
      @apply w-full h-full max-w-full max-h-full;
    }
  }
  &__loader {
    @apply absolute inset-0 z-10 flex items-center justify-center pointer-events-none;
  }
}
</style>
